import React, { useState, useMemo } from 'react';
import { usePIM } from '../../context/PIMContext';

import { Trash, Plus, Settings, FileText, Download, ExternalLink, Edit, Pen, RefreshCw } from 'lucide-react';
import { GhostButton } from '../ui/GhostButton';
import { Badge } from '../ui/Badge';
import { DocumentResourceModal } from '../modals/DocumentResourceModal';
import { LinkDocumentModal } from '../modals/LinkDocumentModal';
import { ReindexModal } from '../modals/ReindexModal';
import { useToast } from '../../context/ToastContext';
import { ResourceTypeManagementModal } from '../modals/ResourceTypeManagementModal';
import * as Icons from 'lucide-react';

import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';
import { RagStatusBadge } from '../ui/RagStatusBadge';

export const DocumentsTab = () => {
    const { toast } = useToast();
    const {
        documentResources,
        deleteDocumentResource,
        updateDocumentResource,
        canEditContent
    } = usePIM();

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
    const [isManageModalOpen, setIsManageModalOpen] = useState(false);
    const [editingResource, setEditingResource] = useState(null);
    const [reindexModalOpen, setReindexModalOpen] = useState(false);
    const [selectedDocForReindex, setSelectedDocForReindex] = useState(null);

    // Scope filter state: 'all', 'charge', 'product'
    const [scopeFilter, setScopeFilter] = useState('all');

    // Sorting
    const { items: sortedResources, requestSort, sortConfig } = useSortableData(documentResources, { key: 'type_sort_order', direction: 'ascending' });

    // Filter by scope
    const filteredResources = useMemo(() => {
        if (scopeFilter === 'all') return sortedResources;
        return sortedResources.filter(d => (d.scope || 'charge') === scopeFilter);
    }, [sortedResources, scopeFilter]);

    const productCount = useMemo(() => sortedResources.filter(d => d.scope === 'product').length, [sortedResources]);
    const chargeCount = useMemo(() => sortedResources.filter(d => d.scope !== 'product').length, [sortedResources]);

    const getIcon = (iconName) => {
        const Icon = Icons[iconName] || Icons.File;
        return <Icon size={16} />;
    };

    const handleEdit = (resource) => {
        setEditingResource(resource);
        setIsAddModalOpen(true);
    };

    const handleCloseAddModal = () => {
        setIsAddModalOpen(false);
        setEditingResource(null);
    };

    const handleVisibilityChange = async (res, newVisibility) => {
        if (!canEditContent) return;
        try {
            await updateDocumentResource(res.id, {
                ...res,
                release_date: res.release_date ? res.release_date.split('T')[0] : null,
                visibility: newVisibility
            });
            toast.success("Visibility updated");
        } catch (err) {
            toast.error("Failed to update visibility");
        }
    };

    return (
        <div className="h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-xl font-bold text-white">Documents</h2>
                    <p className="text-xs text-stone-500 mt-1">Manage product documentation, manuals, and certificates.</p>
                </div>
                {canEditContent && (
                    <div className="flex gap-3">
                        <GhostButton onClick={() => setIsLinkModalOpen(true)}><Icons.Link size={16} /> Add Link</GhostButton>
                        <GhostButton onClick={() => setIsAddModalOpen(true)}><Plus size={16} /> New Document</GhostButton>
                        <GhostButton onClick={() => setIsManageModalOpen(true)}><Settings size={16} /> Manage Types</GhostButton>
                    </div>
                )}
            </div>

            {/* Scope Filter Tabs */}
            <div className="flex items-center gap-2 mb-3">
                <button
                    onClick={() => setScopeFilter('all')}
                    className={`px-3 py-1 rounded text-xs font-semibold transition ${
                        scopeFilter === 'all' 
                            ? 'bg-white/10 text-white border border-white/20' 
                            : 'text-stone-400 hover:text-stone-200 border border-transparent'
                    }`}
                >
                    All ({sortedResources.length})
                </button>
                <button
                    onClick={() => setScopeFilter('charge')}
                    className={`px-3 py-1 rounded text-xs font-semibold transition ${
                        scopeFilter === 'charge' 
                            ? 'bg-blue-900/30 text-blue-300 border border-blue-700/50' 
                            : 'text-stone-400 hover:text-stone-200 border border-transparent'
                    }`}
                >
                    Charge-Specific ({chargeCount})
                </button>
                <button
                    onClick={() => setScopeFilter('product')}
                    className={`px-3 py-1 rounded text-xs font-semibold transition ${
                        scopeFilter === 'product' 
                            ? 'bg-purple-900/40 text-purple-300 border border-purple-600/50' 
                            : 'text-stone-400 hover:text-stone-200 border border-transparent'
                    }`}
                >
                    Product-Wide ({productCount})
                </button>
            </div>

            <div className="bg-black/20 border border-white/5 rounded-xl overflow-hidden shadow-xl flex-1 flex flex-col">
                <div className="overflow-y-auto flex-1">
                    <table className="w-full text-left border-collapse">
                        <thead className="sticky top-0 z-10">
                            <tr>
                                <th className="bg-black/40 text-stone-500 text-xs uppercase font-semibold px-4 py-3 border-b border-white/5 w-10"></th>
                                <SortableHeader label="Name" sortKey="name" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Type" sortKey="type_sort_order" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Scope" sortKey="scope" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Version" sortKey="version" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Date" sortKey="release_date" currentSort={sortConfig} onSort={requestSort} />
                                <th className="bg-black/40 text-stone-500 text-xs uppercase font-semibold px-4 py-3 border-b border-white/5">RAG</th>
                                <SortableHeader label="Visibility" sortKey="visibility" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Path / Link" sortKey="file_path" currentSort={sortConfig} onSort={requestSort} />
                                <th className="bg-black/40 text-stone-500 text-xs uppercase font-semibold px-4 py-3 border-b border-white/5 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredResources.length === 0 ? (
                                <tr>
                                     <td colSpan="10" className="px-4 py-8 text-center text-stone-500 text-sm">
                                        No documents found for this filter.
                                    </td>
                                </tr>
                            ) : (
                                filteredResources.map(res => (
                                    <tr key={res.id} className="hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                                        <td className="px-4 py-0.5 text-stone-400">
                                            {getIcon(res.type_icon)}
                                        </td>
                                        <td className="px-4 py-0.5 text-sm text-stone-200 font-medium">{res.name}</td>
                                        <td className="px-4 py-0.5 text-sm text-stone-400">
                                            <Badge color={res.type_color || 'purple'} size="sm">{res.type}</Badge>
                                        </td>
                                        <td className="px-4 py-0.5">
                                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                                res.scope === 'product'
                                                    ? 'bg-purple-900/40 text-purple-300 border border-purple-700/50'
                                                    : 'bg-stone-800 text-stone-400 border border-stone-700/50'
                                            }`}>
                                                {res.scope === 'product' ? 'Product' : 'Charge'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-0.5 text-sm text-stone-400">{res.version}</td>
                                        <td className="px-4 py-0.5 text-sm text-stone-400">
                                            {res.release_date ? new Date(res.release_date).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-4 py-0.5">
                                            <RagStatusBadge documentId={res.id} />
                                        </td>
                                        <td className="px-4 py-0.5">
                                            {canEditContent ? (
                                                <select
                                                    value={res.visibility || 'sensitive'}
                                                    onChange={(e) => handleVisibilityChange(res, e.target.value)}
                                                    className={`text-[10px] font-bold border rounded px-1 py-0.5 bg-black/40 outline-none cursor-pointer focus:border-blue-500 transition-colors ${
                                                        (res.visibility === 'public') ? 'text-green-400 border-green-800/50' : 
                                                        (res.visibility === 'blocked') ? 'text-red-400 border-red-800/50' :
                                                        'text-amber-500 border-amber-800/50'
                                                    }`}
                                                >
                                                    <option value="public" className="text-green-400 bg-black/20">PUBLIC</option>
                                                    <option value="sensitive" className="text-amber-500 bg-black/20">SENSITIVE</option>
                                                    <option value="blocked" className="text-red-400 bg-black/20">BLOCKED</option>
                                                </select>
                                            ) : (
                                                <span className={`text-[10px] font-bold border rounded px-1.5 py-0.5 ${
                                                    (res.visibility === 'public') ? 'text-green-400 border-green-800/50 bg-green-950/30' : 
                                                    (res.visibility === 'blocked') ? 'text-red-400 border-red-800/50 bg-red-950/30' :
                                                    'text-amber-500 border-amber-800/50 bg-amber-950/30'
                                                }`}>
                                                    {(res.visibility || 'sensitive').toUpperCase()}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-0.5 text-sm text-stone-400 truncate max-w-[100px]" title={res.file_path}>
                                            {res.mime_type === 'link' ? (
                                                <a href={res.file_path} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-400 hover:underline truncate">
                                                    <ExternalLink size={12} className="flex-shrink-0" /> <span className="truncate">{res.file_path}</span>
                                                </a>
                                            ) : (
                                                <span className="flex items-center gap-1 truncate">
                                                    <FileText size={12} className="flex-shrink-0" /> <span className="truncate">{res.file_path}</span>
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-0.5 text-right">
                                            <div className="flex justify-end gap-2">
                                                {res.mime_type !== 'link' && (
                                                    <>
                                                        {canEditContent && (
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedDocForReindex(res);
                                                                    setReindexModalOpen(true);
                                                                }}
                                                                className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-purple-400 hover:border-purple-900/50 hover:bg-purple-900/10 flex items-center justify-center transition"
                                                                title="Re-Index (RAG)"
                                                            >
                                                                <RefreshCw size={14} />
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                const token = localStorage.getItem('token');
                                                                const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : '';
                                                                window.location.href = `/api/charge-documents/${res.id}/download${tokenQuery}`;
                                                            }}
                                                            className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-blue-400 hover:border-blue-900/50 hover:bg-blue-900/10 flex items-center justify-center transition"
                                                            title="Download"
                                                        >
                                                            <Download size={14} />
                                                        </button>
                                                    </>
                                                )}
                                                {canEditContent && res.file_path && res.mime_type !== 'link' && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            const webdavBase = `${window.location.origin}/webdav`;
                                                            const fullPath = res.file_path.startsWith('/') ? res.file_path : `/${res.file_path}`;
                                                            const webdavUrl = `${webdavBase}${fullPath}`;
                                                            const lowerPath = res.file_path.toLowerCase();

                                                            let editUrl = webdavUrl;
                                                            let isOffice = false;
                                                            if (lowerPath.endsWith('.docx') || lowerPath.endsWith('.doc')) {
                                                                editUrl = `ms-word:ofe|u|${webdavUrl}`;
                                                                isOffice = true;
                                                            } else if (lowerPath.endsWith('.xlsx') || lowerPath.endsWith('.xls')) {
                                                                editUrl = `ms-excel:ofe|u|${webdavUrl}`;
                                                                isOffice = true;
                                                            } else if (lowerPath.endsWith('.pptx') || lowerPath.endsWith('.ppt')) {
                                                                editUrl = `ms-powerpoint:ofe|u|${webdavUrl}`;
                                                                isOffice = true;
                                                            }

                                                            if (isOffice) {
                                                                toast.info("Opening document in Microsoft Office...");
                                                                window.location.href = editUrl;
                                                            } else {
                                                                if (window.confirm(`Möchtest du diese Datei lokal über das 'pim-edit' Protokoll in deinem Desktop-Programm ({res.type}) öffnen?\n\nAchtung: Das erfordert, dass das pim-launcher Skript auf deinem Windows-PC installiert ist.`)) {
                                                                    const hostWithoutProtocol = webdavBase.replace('https://', '').replace('http://', '');
                                                                    const protocol = webdavBase.startsWith('https') ? 'https://' : 'http://';
                                                                    const webdavFileUrl = `${protocol}${hostWithoutProtocol}${fullPath}`;
                                                                    editUrl = `pim-edit://${encodeURIComponent(webdavFileUrl)}`;
                                                                    toast.info(`Opening ${res.name} locally...`);
                                                                    window.location.href = editUrl;
                                                                }
                                                            }
                                                        }}
                                                        className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-green-400 hover:border-green-900/50 hover:bg-green-900/10 flex items-center justify-center transition"
                                                        title={`Direct Edit via WebDAV`}
                                                    >
                                                        <Pen size={14} />
                                                    </button>
                                                )}
                                                {canEditContent && (
                                                    <>
                                                        <button
                                                            onClick={() => handleEdit(res)}
                                                            className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-yellow-400 hover:border-yellow-900/50 hover:bg-yellow-900/10 flex items-center justify-center transition"
                                                            title="Edit"
                                                        >
                                                            <Edit size={14} />
                                                        </button>
                                                        <button
                                                            onClick={() => deleteDocumentResource(res.id)}
                                                            className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-red-400 hover:border-red-900/50 hover:bg-red-900/10 flex items-center justify-center transition"
                                                            title="Delete"
                                                        >
                                                            <Trash size={14} />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <LinkDocumentModal
                isOpen={isLinkModalOpen}
                onClose={() => setIsLinkModalOpen(false)}
            />

            <DocumentResourceModal
                isOpen={isAddModalOpen}
                onClose={handleCloseAddModal}
                resourceToEdit={editingResource}
            />

            <ReindexModal
                isOpen={reindexModalOpen}
                onClose={() => setReindexModalOpen(false)}
                document={selectedDocForReindex}
                onReindex={(jobId) => {
                    console.log("Re-indexing started, job:", jobId);
                }}
            />

            <ResourceTypeManagementModal
                isOpen={isManageModalOpen}
                onClose={() => setIsManageModalOpen(false)}
                defaultCategory="Document"
            />
        </div>
    );
};
